import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiFetch } from '../../utils/api';   
import { sanitizeInstruction, sanitizeFoodOrName } from '../../utils/textSanitizer';   

const allergenCategories = [
  { key: 'Pollens', label: 'Pollens' },
  { key: 'Fungi', label: 'Fungi' },
  { key: 'Mites', label: 'Mites' },
  { key: 'Dusts', label: 'Dusts' },
  { key: 'Insects', label: 'Insects' },
  { key: 'Dander/Epithelia', label: 'Dander/Epithelia' },
  { key: 'Foods', label: 'Veg Food' },
  { key: 'Non Jain', label: 'Non Jain' },
  { key: 'Non-Veg', label: 'Non-Veg' },
  { key: 'Miscellaneous', label: 'Miscellaneous' },
  { key: 'Extra Instruction', label: 'Extra Instruction' },
];

const getFoodType = (allergen) => {
  if (!allergen) return '';
  if (allergen.foodCategory) {
    switch (allergen.foodCategory.toLowerCase()) {
      case 'veg': return 'Veg';
      case 'jain': return 'Non Jain';
      case 'non-veg': return 'Non-Veg';
    }
  }
  if (!allergen.name?.english) return '';
  const foodName = allergen.name.english.toLowerCase();
  const nonVegFoods = [
    'chicken', 'beef', 'pork', 'lamb', 'fish', 'shrimp', 'crab', 'lobster',
    'oyster', 'clam', 'mussel', 'scallop', 'squid', 'octopus', 'duck',
    'turkey', 'goose', 'quail', 'pheasant', 'venison', 'rabbit', 'goat',
    'mutton', 'bacon', 'ham', 'sausage', 'pepperoni', 'salami', 'anchovy',
    'tuna', 'salmon', 'cod', 'halibut', 'mackerel', 'sardine', 'herring',
    'trout', 'catfish', 'tilapia', 'swordfish', 'mahi mahi', 'grouper',
    'red snapper', 'sea bass', 'egg', 'eggs', 'yolk', 'albumin',
    'ovalbumin', 'ovomucoid', 'lysozyme'
  ];
  const jainFoods = [
    'potato', 'onion', 'garlic', 'ginger', 'carrot', 'radish', 'turnip',
    'beetroot', 'sweet potato', 'yam', 'taro', 'cassava', 'parsnip',
    'rutabaga', 'celeriac', 'horseradish', 'wasabi', 'leek', 'shallot',
    'chive', 'scallion', 'spring onion', 'asafoetida', 'hing', 'mushroom',
    'truffle', 'morel', 'chanterelle', 'shiitake', 'oyster mushroom',
    'portobello', 'cremini', 'enoki', 'maitake', 'reishi'
  ];
  if (nonVegFoods.some(food => foodName.includes(food))) return 'Non-Veg';
  if (jainFoods.some(food => foodName.includes(food))) return 'Non Jain';
  return 'Veg';
};

const categoryOrder = [
  'pollens',
  'fungi',
  'mites',
  'dusts',
  'insects',
  'dander/epithelia',
  'foods',
  'miscellaneous',
];

const LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'gujarati', label: 'Gujarati' },
  { value: 'marathi', label: 'Marathi' },
];

// Utility to fetch patient image as base64
async function fetchPatientImageBase64(patientId) {
  const response = await fetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients/fori/${patientId}`);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

const AddInstruction = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(allergenCategories[0].key);
  const [allergies, setAllergies] = useState([]);
  const [extraInstructions, setExtraInstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInstructions, setSelectedInstructions] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [hasUserModified, setHasUserModified] = useState(false);
  const [defaultInstructionKeys, setDefaultInstructionKeys] = useState([]);
  const [defaultImageIds, setDefaultImageIds] = useState([]);
  const [hasPatchTest, setHasPatchTest] = useState(false);

  // Extra instruction modal states
  const [extraModalOpen, setExtraModalOpen] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [extraForm, setExtraForm] = useState({ english: '', hindi: '', gujarati: '', marathi: '' });
  const [extraSaving, setExtraSaving] = useState(false);

  const instructionsScrollRef = useRef(null);
  const imagesScrollRef = useRef(null);

  useEffect(() => {
    if (instructionsScrollRef.current) {
      instructionsScrollRef.current.scrollTop = 0;
    }
    if (imagesScrollRef.current) {
      imagesScrollRef.current.scrollTop = 0;
    }
  }, [selectedCategory]);

  const fetchExtraInstructions = async () => {
    try {
      const res = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/extra-instructions`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.success && Array.isArray(data.data)) {
          setExtraInstructions(data.data);
          return data.data;
        }
      }
    } catch (err) {
      console.error('Failed to fetch extra instructions:', err);
    }
    return [];
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchData = async () => {
      try {
        const [allergiesRes, existingRes, extraRes, patchRes] = await Promise.all([
          apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/allergies/all`),
          patientId ? apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/save-instruction/by-patient/${patientId}`) : Promise.resolve(null),
          apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/extra-instructions`),
          patientId ? apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patch-testing/patient/${patientId}`) : Promise.resolve(null)
        ]);

        let fetchedAllergies = [];
        if (allergiesRes.ok) {
          const data = await allergiesRes.json();
          if (data && data.success && Array.isArray(data.data)) {
            fetchedAllergies = data.data;
            setAllergies(fetchedAllergies);
          } else {
            setAllergies([]);
            setError('No data found.');
          }
        } else {
          setError('Failed to fetch allergies');
          setAllergies([]);
        }

        let fetchedExtra = [];
        if (extraRes && extraRes.ok) {
          const extraData = await extraRes.json();
          if (extraData && extraData.success && Array.isArray(extraData.data)) {
            fetchedExtra = extraData.data;
            setExtraInstructions(fetchedExtra);
          }
        }

        // Process Allergy Test data for qualifying allergies (Enythema-1 >= 3)
        const qualifyingAllergenIds = new Set();
        const qualifyingAllergenNames = new Set();
        let foundPatchTest = false;

        if (patchRes && patchRes.ok) {
          try {
            const patchData = await patchRes.json();
            if (Array.isArray(patchData) && patchData.length > 0) {
              foundPatchTest = true;
              const latestReport = patchData[0];
              if (latestReport && latestReport.allergens && typeof latestReport.allergens === 'object') {
                Object.values(latestReport.allergens).forEach(categoryItems => {
                  if (Array.isArray(categoryItems)) {
                    categoryItems.forEach(item => {
                      const val = item.val !== undefined && item.val !== null ? Number(item.val) : 0;
                      if (val >= 3) {
                        const id = item.allergenId?._id
                          ? String(item.allergenId._id)
                          : (item.allergenId ? String(item.allergenId) : '');
                        if (id) qualifyingAllergenIds.add(id);
                        if (item.name) qualifyingAllergenNames.add(item.name.trim().toLowerCase());
                      }
                    });
                  }
                });
              }
            }
          } catch (patchErr) {
            console.error('Error parsing patch test results:', patchErr);
          }
        }
        setHasPatchTest(foundPatchTest);

        // Precompute default instruction keys and image IDs for qualifying allergies (val >= 3)
        const computedDefaultKeys = [];
        const computedDefaultImageIds = [];
        fetchedAllergies.forEach(allergy => {
          const allergyId = String(allergy._id);
          const allergyNameEng = (typeof allergy.name === 'object' ? allergy.name.english : allergy.name) || '';
          const isQualifying = qualifyingAllergenIds.has(allergyId) ||
            (allergyNameEng && qualifyingAllergenNames.has(allergyNameEng.trim().toLowerCase()));

          if (isQualifying) {
            const cat = (allergy.category || '').toLowerCase();
            if (cat === 'foods' || cat === 'miscellaneous') {
              computedDefaultKeys.push(`${allergy._id}-0`);
            } else if (Array.isArray(allergy.instructions)) {
              allergy.instructions.forEach((_, idx) => {
                computedDefaultKeys.push(`${allergy._id}-${idx}`);
              });
            }

            // Auto-select image if allergy has one
            const hasImg = Boolean(allergy.hasImage || (allergy.image && (allergy.image.hasImage || (allergy.image.data && allergy.image.data !== ''))));
            if (hasImg) {
              computedDefaultImageIds.push(allergy._id);
            }
          }
        });
        setDefaultInstructionKeys(computedDefaultKeys);
        setDefaultImageIds(computedDefaultImageIds);

        let savedDoc = null;
        if (existingRes && existingRes.ok) {
          const responseData = await existingRes.json();
          if (responseData && responseData.success && Array.isArray(responseData.data) && responseData.data.length > 0) {
            savedDoc = responseData.data[0];
          }
        }

        if (savedDoc) {
          if (savedDoc.language) {
            setSelectedLanguage(savedDoc.language);
          }

          // Check whether the user has manually modified instructions/images previously
          // For legacy records where hasUserModified is undefined, treat as true to preserve existing saved instructions
          const isUserModified = savedDoc.hasUserModified !== undefined ? Boolean(savedDoc.hasUserModified) : true;

          if (isUserModified) {
            if (Array.isArray(savedDoc.allergiesImage)) {
              const imageIds = savedDoc.allergiesImage.map(img => typeof img === 'object' && img._id ? String(img._id) : String(img));
              setSelectedImages(imageIds);
            }

            const restoredInstructions = [];
            const savedInstructions = savedDoc.instructions || [];
            const savedFoods = savedDoc.foods || [];

            fetchedAllergies.forEach(allergy => {
              const cat = (allergy.category || '').toLowerCase();
              if (cat === 'foods' || cat === 'miscellaneous') {
                const isFoodChecked = savedFoods.some(food => {
                  const foodEng = (typeof food === 'object' ? food.english : food) || '';
                  const allergyEng = (typeof allergy.name === 'object' ? allergy.name.english : allergy.name) || '';
                  return foodEng.trim().toLowerCase() === allergyEng.trim().toLowerCase();
                });
                if (isFoodChecked) {
                  restoredInstructions.push(`${allergy._id}-0`);
                }
              } else {
                if (Array.isArray(allergy.instructions)) {
                  allergy.instructions.forEach((inst, idx) => {
                    const instEng = (typeof inst === 'object' ? inst.english : inst) || '';
                    const isInstChecked = savedInstructions.some(sInst => {
                      const sEng = (typeof sInst === 'object' ? sInst.english : sInst) || '';
                      return sEng.trim().toLowerCase() === instEng.trim().toLowerCase();
                    });
                    if (isInstChecked) {
                      restoredInstructions.push(`${allergy._id}-${idx}`);
                    }
                  });
                }
              }
            });

            // Restore extra instructions
            fetchedExtra.forEach(extra => {
              const isExtraChecked = savedInstructions.some(sInst => {
                const sEng = (typeof sInst === 'object' ? sInst.english : sInst) || '';
                return sEng.trim().toLowerCase() === (extra.english || '').trim().toLowerCase();
              });
              if (isExtraChecked) {
                restoredInstructions.push(`extra-${extra._id}`);
              }
            });

            setSelectedInstructions(restoredInstructions);
            setHasUserModified(true);
          } else {
            // Not manually modified: use default qualifying selections from Allergy Test for both instructions and images
            setSelectedInstructions(computedDefaultKeys);
            setSelectedImages(computedDefaultImageIds);
            setHasUserModified(false);
          }
        } else {
          // No saved instructions: default to automatic selections based on Allergy Test results
          setSelectedInstructions(computedDefaultKeys);
          setSelectedImages(computedDefaultImageIds);
          setHasUserModified(false);
        }
      } catch (err) {
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [patientId]);

  const handleInstructionCheckbox = (allergyId, instructionIndex) => {
    setHasUserModified(true);
    const instructionKey = `${allergyId}-${instructionIndex}`;
    setSelectedInstructions(prev =>
      prev.includes(instructionKey)
        ? prev.filter(key => key !== instructionKey)
        : [...prev, instructionKey]
    );
  };

  const handleResetToDefaults = () => {
    setSelectedInstructions(defaultInstructionKeys);
    setSelectedImages(defaultImageIds);
    setHasUserModified(false);
    toast.info('Selections reset to Allergy Test results (Enythema-1 ≥ 3)');
  };

  const handleImageCheckbox = (allergyId) => {
    setHasUserModified(true);
    setSelectedImages(prev =>
      prev.includes(allergyId)
        ? prev.filter(id => id !== allergyId)
        : [...prev, allergyId]
    );
  };

  const allergySrMap = React.useMemo(() => {
    const grouped = {};
    allergies.forEach(item => {
      const cat = (item.category || '').toLowerCase();
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });

    const srMap = {};
    let currentSr = 1;

    const categories = [
      'pollens',
      'fungi',
      'mites',
      'dusts',
      'insects',
      'dander/epithelia',
    ];

    categories.forEach(cat => {
      if (grouped[cat]) {
        grouped[cat].forEach(item => {
          srMap[item._id] = currentSr++;
        });
      }
    });

    // Foods split
    const foods = grouped['foods'] || [];
    const vegFoods = foods.filter(a => getFoodType(a) === 'Veg');
    const jainFoods = foods.filter(a => getFoodType(a) === 'Non Jain');
    const nonVegFoods = foods.filter(a => getFoodType(a) === 'Non-Veg');

    vegFoods.forEach(item => { srMap[item._id] = currentSr++; });
    jainFoods.forEach(item => { srMap[item._id] = currentSr++; });
    nonVegFoods.forEach(item => { srMap[item._id] = currentSr++; });

    // Miscellaneous
    const misc = grouped['miscellaneous'] || [];
    misc.forEach(item => { srMap[item._id] = currentSr++; });

    return srMap;
  }, [allergies]);

  const isExtraCategory = selectedCategory === 'Extra Instruction';
  const isFoodOrMisc = !isExtraCategory && ['foods', 'non jain', 'non-veg', 'miscellaneous'].includes(selectedCategory.toLowerCase());

  const displayedAllergies = isExtraCategory
    ? []
    : allergies.filter(allergy => {
        const allergyCategory = (allergy.category || '').toLowerCase();
        const selCat = selectedCategory.toLowerCase();

        if (['foods', 'non jain', 'non-veg'].includes(selCat)) {
          if (allergyCategory !== 'foods') return false;
          const foodType = getFoodType(allergy);
          if (selCat === 'foods') return foodType === 'Veg';
          if (selCat === 'non jain') return foodType === 'Non Jain';
          if (selCat === 'non-veg') return foodType === 'Non-Veg';
          return false;
        }

        return allergyCategory === selCat;
      });

  const handleOpenAddExtra = () => {
    setEditingExtra(null);
    setExtraForm({ english: '', hindi: '', gujarati: '', marathi: '' });
    setExtraModalOpen(true);
  };

  const handleOpenEditExtra = (extra) => {
    setEditingExtra(extra);
    setExtraForm({
      english: extra.english || '',
      hindi: extra.hindi || '',
      gujarati: extra.gujarati || '',
      marathi: extra.marathi || '',
    });
    setExtraModalOpen(true);
  };

  const handleSaveExtra = async (e) => {
    if (e) e.preventDefault();
    if (!extraForm.english || !extraForm.english.trim()) {
      toast.error('English instruction is required');
      return;
    }
    setExtraSaving(true);
    try {
      const url = editingExtra
        ? `${process.env.REACT_APP_CLIENT_BASE_URL}/api/extra-instructions/${editingExtra._id}`
        : `${process.env.REACT_APP_CLIENT_BASE_URL}/api/extra-instructions`;
      const method = editingExtra ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(extraForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(editingExtra ? 'Extra instruction updated!' : 'Extra instruction added!');
        setExtraModalOpen(false);
        if (!editingExtra && data.data && data.data._id) {
          setHasUserModified(true);
          setSelectedInstructions(prev => [...prev, `extra-${data.data._id}`]);
        }
        await fetchExtraInstructions();
      } else {
        toast.error(data.message || 'Failed to save extra instruction');
      }
    } catch (err) {
      toast.error('Failed to save extra instruction');
    } finally {
      setExtraSaving(false);
    }
  };

  const handleDeleteExtra = async (id) => {
    if (!window.confirm('Are you sure you want to delete this extra instruction?')) {
      return;
    }
    try {
      const res = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/extra-instructions/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Extra instruction deleted');
        setHasUserModified(true);
        setSelectedInstructions(prev => prev.filter(key => key !== `extra-${id}`));
        await fetchExtraInstructions();
      } else {
        toast.error(data.message || 'Failed to delete extra instruction');
      }
    } catch (err) {
      toast.error('Failed to delete extra instruction');
    }
  };

  const handleSave = async (language) => {
    let allInstructions = [];
    let allFoods = [];
    let allAllergyImages = [];

    categoryOrder.forEach(cat => {
      allergies
        .filter(a => a.category.toLowerCase() === cat)
        .forEach(allergy => {
          const instructionIndexes = (allergy.instructions || []).map((_, idx) => `${allergy._id}-${idx}`);
          const checkedInstructions = instructionIndexes.filter(key => selectedInstructions.includes(key));
          const imageChecked = selectedImages.includes(allergy._id);

          if (cat === 'foods' || cat === 'miscellaneous') {
            if (selectedInstructions.includes(`${allergy._id}-0`)) {
              const foodNameObj = typeof allergy.name === 'object' ? allergy.name : { english: allergy.name };
              allFoods.push({
                english: sanitizeFoodOrName(foodNameObj.english || ''),
                hindi: sanitizeFoodOrName(foodNameObj.hindi || ''),
                gujarati: sanitizeFoodOrName(foodNameObj.gujarati || ''),
                marathi: sanitizeFoodOrName(foodNameObj.marathi || '')
              });
            }
            if (imageChecked) {
              allAllergyImages.push(allergy._id);
            }
          } else {
            if (checkedInstructions.length > 0) {
              checkedInstructions.forEach(key => {
                const idx = parseInt(key.split('-')[1], 10);
                const instruction = allergy.instructions[idx];
                if (instruction && typeof instruction === 'object') {
                  allInstructions.push({
                    english: sanitizeInstruction(instruction.english || '', 'english'),
                    hindi: sanitizeInstruction(instruction.hindi || '', 'hindi'),
                    gujarati: sanitizeInstruction(instruction.gujarati || '', 'gujarati'),
                    marathi: sanitizeInstruction(instruction.marathi || '', 'marathi')
                  });
                } else if (instruction) {
                  allInstructions.push(sanitizeInstruction(instruction, selectedLanguage));
                }
              });
            }
            if (imageChecked) {
              allAllergyImages.push(allergy._id);
            }
          }
        });
    });

    // Append selected Extra Instructions at the end of allInstructions
    const selectedExtraIds = selectedInstructions
      .filter(key => typeof key === 'string' && key.startsWith('extra-'))
      .map(key => key.replace('extra-', ''));

    extraInstructions
      .filter(extra => selectedExtraIds.includes(extra._id))
      .forEach(extra => {
        allInstructions.push({
          english: sanitizeInstruction(extra.english || '', 'english'),
          hindi: sanitizeInstruction(extra.hindi || '', 'hindi'),
          gujarati: sanitizeInstruction(extra.gujarati || '', 'gujarati'),
          marathi: sanitizeInstruction(extra.marathi || '', 'marathi')
        });
      });

    allAllergyImages = Array.from(new Set(allAllergyImages));

    const dataToSave = [{
      allergenId: allAllergyImages,
      instructions: allInstructions,
      foods: allFoods
    }];

    try {
      const res = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/save-instruction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient: patientId, data: dataToSave, language: selectedLanguage, hasUserModified })
      });
      if (res.status === 409) {
        const result = await res.json();
        const overwriteRes = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/save-instruction`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ patient: patientId, data: dataToSave, overwrite: true, language: selectedLanguage, hasUserModified })
        });
        if (overwriteRes.ok) {
          toast.success('Instructions saved successfully! Generating report...');
          setTimeout(() => navigate(`/display-instruction/${patientId}`, { state: { selectedLanguage } }), 1200);
        } else {
          toast.error('Failed to overwrite instructions');
        }
      } else if (res.ok) {
        toast.success('Instructions saved successfully! Generating report...');
        setTimeout(() => navigate(`/display-instruction/${patientId}`, { state: { selectedLanguage } }), 1200);
      } else {
        toast.error('Failed to save instructions');
      }
    } catch (err) {
      toast.error('Failed to save instructions');
    }
  };

  const handleDownloadExisting = async (existingId) => {
    navigate(`/display-instruction/${patientId}`);
  };

  const getAllergyNameEnglish = (allergy) => {
    if (!allergy || !allergy.name) return '';
    if (typeof allergy.name === 'string') return allergy.name;
    return allergy.name.english || Object.values(allergy.name)[0] || '';
  };

  const getInstructionInLanguage = (inst, lang) => {
    if (!inst) return '';
    let val = '';
    if (typeof inst === 'string') {
      val = lang === 'english' ? inst : '';
    } else if (lang === 'english') {
      val = inst.english || '';
    } else {
      val = inst[lang] && typeof inst[lang] === 'string' && inst[lang].trim() !== '' ? inst[lang] : '';
    }
    if (!val || !val.replace(/[.,;:!?।॥\s\-_/\\()[\]{}'"`~@#$%^&*+=<>|]/g, '') || val.trim().toLowerCase() === 'not provided') {
      return '';
    }
    return sanitizeInstruction(val, lang);
  };

  const getExtraInstructionInLanguage = (extra, lang) => {
    if (!extra) return '';
    let val = '';
    if (lang === 'english') {
      val = extra.english || '';
    } else {
      val = extra[lang] && typeof extra[lang] === 'string' && extra[lang].trim() !== '' ? extra[lang] : '';
    }
    if (!val || !val.replace(/[.,;:!?।॥\s\-_/\\()[\]{}'"`~@#$%^&*+=<>|]/g, '') || val.trim().toLowerCase() === 'not provided') {
      return '';
    }
    return sanitizeInstruction(val, lang);
  };

  const allInstructionKeys = isExtraCategory
    ? extraInstructions
        .filter(extra => {
          const text = getExtraInstructionInLanguage(extra, selectedLanguage);
          return text && text.trim() !== '';
        })
        .map(extra => `extra-${extra._id}`)
    : displayedAllergies.flatMap(allergy => {
        if (isFoodOrMisc) {
          // Only include if name is available in selected language
          const hasLocalizedName = selectedLanguage === 'english'
            ? !!getAllergyNameEnglish(allergy)
            : !!(allergy.name && typeof allergy.name === 'object' && allergy.name[selectedLanguage] && allergy.name[selectedLanguage].trim() !== '');
          return hasLocalizedName ? [`${allergy._id}-0`] : [];
        } else if (Array.isArray(allergy.instructions)) {
          return allergy.instructions
            .map((inst, instIdx) => {
              const text = getInstructionInLanguage(inst, selectedLanguage);
              return text && text.trim() !== '' ? `${allergy._id}-${instIdx}` : null;
            })
            .filter(key => key !== null);
        }
        return [];
      });

  const allImageIds = isExtraCategory
    ? []
    : displayedAllergies
        .filter(a => a.hasImage || (a.image && (a.image.hasImage || (a.image.data && a.image.data !== ''))))
        .map(a => a._id);

  return (
    <div className="container-fluid" style={{ padding: 0, height: '100vh', display: 'flex', flexDirection: 'column', background: '#f7f8fa', overflow: 'hidden' }}>
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
      <div className="row flex-grow-1" style={{ margin: 0, height: '100%', overflow: 'hidden' }}>
        {/* Sidebar: Categories */}
        <div className="col-md-2 bg-white shadow-sm" style={{ padding: 0, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="p-3 custom-scrollbar" style={{ flex: 1, overflowY: 'auto' }}>
            <h5 className="fw-bold mb-3" style={{ fontSize: 18 }}>Categories</h5>
            <div className="d-flex flex-column gap-2">
              {allergenCategories.map((cat) => (
                <button
                  key={cat.key}
                  className={`btn btn-sm ${selectedCategory === cat.key ? 'btn-primary' : 'btn-outline-primary'}`}
                  style={{ textAlign: 'left', borderRadius: 8 }}
                  onClick={() => setSelectedCategory(cat.key)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Main Panel: Allergies and Instructions */}
        <div className="col-md-7 d-flex flex-column" style={{ padding: '20px 20px 0px 32px', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span
                className="fa fa-arrow-left me-3"
                style={{ fontSize: 20, color: '#0f4c75', cursor: 'pointer', marginRight: 16 }}
                onClick={() => navigate('/patients')}
              ></span>
              <span style={{ fontWeight: 600, fontSize: 18, color: '#0f4c75', marginRight: 18 }}>Add Instructions</span>
              <span style={{ background: '#e3f0ff', color: '#1761a0', borderRadius: 8, padding: '4px 14px', fontWeight: 600, fontSize: 15 }}>
                Patient ID: {patientId}
              </span>
              {hasPatchTest && (
                <span
                  className={`badge ms-2 ${hasUserModified ? 'bg-secondary' : 'bg-success'}`}
                  style={{ fontSize: 12, fontWeight: 500, padding: '6px 10px', borderRadius: 6 }}
                  title={hasUserModified ? 'Instructions have been manually modified' : 'Instructions automatically selected from Allergy Test results (Enythema-1 ≥ 3)'}
                >
                  {hasUserModified ? 'Manually Modified' : 'Auto-selected (Enythema ≥ 3)'}
                </span>
              )}
            </div>
            {hasPatchTest && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary ms-auto"
                style={{ borderRadius: 8, fontWeight: 500, fontSize: 13, padding: '4px 12px' }}
                onClick={handleResetToDefaults}
                title="Reset instruction selections to automatic selections based on Allergy Test results (Enythema-1 ≥ 3)"
              >
                <i className="fa fa-refresh me-1"></i> Reset to Test Defaults
              </button>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="language-select" className="form-label fw-bold">Instruction Language for PDF:</label>
            <select
              id="language-select"
              className="form-select"
              value={selectedLanguage}
              onChange={e => setSelectedLanguage(e.target.value)}
              style={{ maxWidth: 220 }}
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {/* Allergies and Instructions */}
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : isExtraCategory ? (
            /* Extra Instruction View */
            <Fragment>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0" style={{ color: '#0f4c75' }}>Extra Instructions</h5>
                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-sm btn-primary" onClick={handleOpenAddExtra}>
                    <i className="fa fa-plus me-1"></i> Add Instruction
                  </button>
                  {allInstructionKeys.length > 0 && (
                    <Fragment>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setHasUserModified(true);
                          setSelectedInstructions(prev => Array.from(new Set([...prev, ...allInstructionKeys])));
                        }}
                      >
                        Select All
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setHasUserModified(true);
                          setSelectedInstructions(prev => prev.filter(key => !allInstructionKeys.includes(key)));
                        }}
                      >
                        Clear
                      </button>
                    </Fragment>
                  )}
                </div>
              </div>
              {extraInstructions.length === 0 ? (
                <div className="alert alert-info d-flex justify-content-between align-items-center" style={{ margin: '24px 0' }}>
                  <span>No extra instructions created yet. Click "Add Instruction" to create one.</span>
                  <button className="btn btn-sm btn-primary" onClick={handleOpenAddExtra}>
                    <i className="fa fa-plus me-1"></i> Add Instruction
                  </button>
                </div>
              ) : (
                <div ref={instructionsScrollRef} className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: 10, paddingBottom: 20, minHeight: 0 }}>
                  <div className="d-flex flex-column gap-3">
                    {extraInstructions.map((extra, idx) => {
                      const text = getExtraInstructionInLanguage(extra, selectedLanguage);
                      const hasText = text && text.trim() !== '';
                      const isChecked = selectedInstructions.includes(`extra-${extra._id}`);

                      return (
                        <div key={extra._id} className="card shadow-sm" style={{ borderRadius: 12, padding: 0, border: '1px solid #e3e3e3' }}>
                          <div className="card-body" style={{ padding: 18 }}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="fw-bold mb-0" style={{ color: '#222' }}>
                                {idx + 1}. Extra Instruction
                              </h6>
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  style={{ padding: '2px 10px', fontSize: 13 }}
                                  title="Edit instruction"
                                  onClick={() => handleOpenEditExtra(extra)}
                                >
                                  <i className="fa fa-pencil me-1"></i> Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  style={{ padding: '2px 10px', fontSize: 13 }}
                                  title="Delete instruction"
                                  onClick={() => handleDeleteExtra(extra._id)}
                                >
                                  <i className="fa fa-trash me-1"></i> Delete
                                </button>
                              </div>
                            </div>

                            <div className="d-flex align-items-start mt-2">
                              {hasText ? (
                                <Fragment>
                                  <input
                                    type="checkbox"
                                    className="me-2 mt-1"
                                    style={{ width: 20, height: 20, minWidth: 20, cursor: 'pointer' }}
                                    checked={isChecked}
                                    onChange={() => {
                                      const key = `extra-${extra._id}`;
                                      setHasUserModified(true);
                                      setSelectedInstructions(prev =>
                                        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                                      );
                                    }}
                                  />
                                  <div>
                                    <span style={{ fontSize: 14 }}>{text}</span>
                                    {selectedLanguage !== 'english' && extra.english && (
                                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                        <strong>English:</strong> {extra.english}
                                      </div>
                                    )}
                                  </div>
                                </Fragment>
                              ) : (
                                <div>
                                  <span className="text-muted" style={{ fontSize: 13 }}>
                                    No instructions available in {selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)}
                                  </span>
                                  {extra.english && (
                                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                      <strong>English:</strong> {extra.english}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Fragment>
          ) : displayedAllergies.length === 0 ? (
            <div className="alert alert-info" style={{ margin: '24px 0' }}>No allergies found for this category.</div>
          ) : (
            <Fragment>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0" style={{ color: '#0f4c75' }}>{selectedCategory} Allergies</h5>
                {allInstructionKeys.length > 0 && (
                  <div>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => {
                      setHasUserModified(true);
                      setSelectedInstructions(prev => Array.from(new Set([...prev, ...allInstructionKeys])));
                    }}>
                      Select All
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                      setHasUserModified(true);
                      setSelectedInstructions(prev => prev.filter(key => !allInstructionKeys.includes(key)));
                    }}>
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div ref={instructionsScrollRef} className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: 10, paddingBottom: 20, minHeight: 0 }}>
                <div className="d-flex flex-column gap-3">
                  {displayedAllergies.map((allergy, idx) => {
                    const allergySr = allergySrMap[allergy._id] || (idx + 1);

                    // For food/misc: compute localized name up front
                    const localizedFoodName = isFoodOrMisc
                      ? (selectedLanguage === 'english'
                          ? getAllergyNameEnglish(allergy)
                          : (allergy.name && typeof allergy.name === 'object' && allergy.name[selectedLanguage] && allergy.name[selectedLanguage].trim() !== '')
                            ? allergy.name[selectedLanguage]
                            : null)
                      : null;

                    // If food/misc and no localized name available, skip this card entirely
                    if (isFoodOrMisc && !localizedFoodName) return null;

                    // For instructions: compute available instructions up front
                    const availableInstructions = !isFoodOrMisc && Array.isArray(allergy.instructions)
                      ? allergy.instructions
                          .map((inst, instIdx) => ({ inst, instIdx, text: getInstructionInLanguage(inst, selectedLanguage) }))
                          .filter(item => item.text && item.text.trim() !== '')
                      : [];

                    return (
                      <div key={allergy._id} className="card shadow-sm" style={{ borderRadius: 12, padding: 0, border: '1px solid #e3e3e3' }}>
                        <div className="card-body" style={{ padding: 20 }}>
                          <h6 className="fw-bold mb-2" style={{ color: '#222' }}>
                            {allergySr}. {getAllergyNameEnglish(allergy)}
                          </h6>
                          {isFoodOrMisc ? (
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className="me-2"
                                style={{ width: 22, height: 22 }}
                                checked={selectedInstructions.includes(`${allergy._id}-0`)}
                                onChange={() => handleInstructionCheckbox(allergy._id, 0)}
                              />
                              <span style={{ fontSize: 13 }}>{localizedFoodName}</span>
                            </div>
                          ) : (
                            !Array.isArray(allergy.instructions) || allergy.instructions.length === 0
                              ? <span className="text-muted" style={{ fontSize: 13 }}>No instructions available</span>
                              : availableInstructions.length === 0
                                ? <span className="text-muted" style={{ fontSize: 13 }}>No instructions available in {selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)}</span>
                                : (
                                  <ul className="list-group list-group-flush mb-0" style={{ border: 'none' }}>
                                    {availableInstructions.map(({ instIdx, text }) => (
                                      <li
                                        className="px-2 py-1 d-flex align-items-center"
                                        key={instIdx}
                                        style={{ fontSize: 13, border: 'none', background: 'none' }}
                                      >
                                        <input
                                          type="checkbox"
                                          className="me-2"
                                          style={{ width: 22, height: 22 }}
                                          checked={selectedInstructions.includes(`${allergy._id}-${instIdx}`)}
                                          onChange={() => handleInstructionCheckbox(allergy._id, instIdx)}
                                        />
                                        <span>
                                          <strong>{`${allergySr}${String.fromCharCode(65 + instIdx)}`}</strong>: {text}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Fragment>
          )}
        </div>
        {/* Right Panel: Images */}
        <div className="col-md-3 bg-white shadow-sm d-flex flex-column" style={{ padding: '32px 16px 0px 16px', borderLeft: '1px solid #eee', height: '100%' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0" style={{ color: '#0f4c75' }}>Select Images</h5>
            {!isExtraCategory && allImageIds.length > 0 && !loading && !error && (
              <div>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => {
                    setHasUserModified(true);
                    setSelectedImages(prev => Array.from(new Set([...prev, ...allImageIds])));
                  }}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setHasUserModified(true);
                    setSelectedImages(prev => prev.filter(id => !allImageIds.includes(id)));
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : isExtraCategory ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888', padding: 20, textAlign: 'center' }}>
              <i className="fa fa-picture-o mb-3" style={{ fontSize: 42, color: '#b0bec5' }}></i>
              <p style={{ fontSize: 14, margin: 0, fontWeight: 600, color: '#546e7a' }}>No images for Extra Instructions</p>
              <span style={{ fontSize: 12, color: '#90a4ae', marginTop: 4 }}>Extra instructions are text-only guidance</span>
            </div>
          ) : displayedAllergies.length === 0 ? (
            <div className="alert alert-info" style={{ margin: '24px 0' }}>No images found for this category.</div>
          ) : (
            <div ref={imagesScrollRef} className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: 5, paddingBottom: 20, minHeight: 0 }}>
              <div className="d-flex flex-column gap-3">
                {displayedAllergies.map((allergy) => {
                  const hasImg = Boolean(allergy.hasImage || (allergy.image && (allergy.image.hasImage || (allergy.image.data && allergy.image.data !== ''))));
                  const isImageChecked = selectedImages.includes(allergy._id);
                  const imageUrl = allergy.image && allergy.image.data && allergy.image.data !== ''
                    ? `data:${allergy.image.contentType || 'image/jpeg'};base64,${allergy.image.data}`
                    : `${process.env.REACT_APP_CLIENT_BASE_URL}/api/allergies/image-file/${allergy._id}`;

                  return (
                    <div key={allergy._id} className="card p-2 mb-2" style={{ borderRadius: 10, border: '1px solid #e3e3e3' }}>
                      <div className="d-flex align-items-center mb-2">
                        {hasImg && (
                          <input
                            type="checkbox"
                            className="me-2"
                            style={{ width: 22, height: 22, cursor: 'pointer' }}
                            checked={isImageChecked}
                            onChange={() => handleImageCheckbox(allergy._id)}
                          />
                        )}
                        <span style={{ fontSize: 13, fontWeight: isImageChecked ? 600 : 400 }}>{getAllergyNameEnglish(allergy)}</span>
                      </div>
                      {hasImg ? (
                        <img
                          src={imageUrl}
                          alt={getAllergyNameEnglish(allergy)}
                          className="w-100"
                          style={{ objectFit: 'cover', height: 120, borderRadius: 8, background: '#f5f5f5' }}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          style={{
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 8,
                            color: '#aaa',
                            fontSize: 13,
                            background: 'none'
                          }}
                        >
                          No Image
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Sticky Save Bar */}
      <div className="sticky-bottom-bar bg-white shadow-lg" style={{ padding: '16px 32px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 12, zIndex: 10 }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
        <button className="btn btn-primary" onClick={() => handleSave(selectedLanguage)}>Save Instructions</button>
      </div>

      {/* Add / Edit Extra Instruction Modal */}
      {extraModalOpen && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content" style={{ borderRadius: 12, border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #eee', padding: '16px 24px' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#0f4c75' }}>
                  {editingExtra ? 'Edit Extra Instruction' : 'Add New Extra Instruction'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setExtraModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleSaveExtra}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px 24px' }}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">English Instruction <span className="text-danger">*</span></label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Enter instruction in English"
                      value={extraForm.english}
                      onChange={e => setExtraForm(prev => ({ ...prev, english: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Hindi Instruction</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Enter instruction in Hindi (optional)"
                      value={extraForm.hindi}
                      onChange={e => setExtraForm(prev => ({ ...prev, hindi: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Gujarati Instruction</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Enter instruction in Gujarati (optional)"
                      value={extraForm.gujarati}
                      onChange={e => setExtraForm(prev => ({ ...prev, gujarati: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Marathi Instruction</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Enter instruction in Marathi (optional)"
                      value={extraForm.marathi}
                      onChange={e => setExtraForm(prev => ({ ...prev, marathi: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '14px 24px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setExtraModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={extraSaving}
                  >
                    {extraSaving ? 'Saving...' : editingExtra ? 'Update Instruction' : 'Save Instruction'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddInstruction;